import {useEffect, useState} from 'react'
import {useAuth} from '../../../../modules/auth'
import db from '../../../../../services/firebase'
import {collection, doc, onSnapshot} from 'firebase/firestore'
import useCrispChat from 'app/Hooks/useCrispChat'
import useAuditContext from 'app/Hooks/useAuditContext'
import {getReviewStatus} from '../../utils/reviewStatusUtils'
type Framework = 'ISO 27001:2022' | 'SOC 2' | 'GDPR'

const DashboardHeader = () => {
  const {currentUser} = useAuth()
  const [totalTest, setTotalTests] = useState(0)
  const [passingTests, setPassingTests] = useState(0)
  const [failingTests, setFailingTests] = useState(0)
  const [totalISO, setTotalISO] = useState(0)
  const [totalSOC, setTotalSOC] = useState(0)
  const [totalGDPR, setTotalGDPR] = useState(0)
  const [totalHIPAA, setTotalHIPAA] = useState(0)
  const [unhealthyISOControlsCount, setUnhealthyISOControlsCount] = useState(0)
  const [unhealthySOCControlsCount, setUnhealthySOCControlsCount] = useState(0)
  const [unhealthyGDPRControlsCount, setUnhealthyGDPRControlsCount] = useState(0)
  const [unhealthyHIPAAControlsCount, setUnhealthyHIPAAControlsCount] = useState(0)
  const [healthyISOControlsCount, setHealthyISOControlsCount] = useState(0)
  const [healthySOCControlsCount, setHealthySOCControlsCount] = useState(0)
  const [healthyGDPRControlsCount, setHealthyGDPRControlsCount] = useState(0)
  const [healthyHIPAAControlsCount, setHealthyHIPAAControlsCount] = useState(0)
  const [activeFrameworks, setActiveFrameworks] = useState<Framework[]>([])
  const [audits, setAudits] = useState<any[]>([])
  const [tests, setTests] = useState<any[]>([])
  const {toggleCrispChat} = useCrispChat()
  const {currentAuditContext} = useAuditContext()

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'audits')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      let Data = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          status: doc.data()?.status,
          createdAt: doc.data().createdAt,
          auditor: doc.data().auditor, // Keep the full auditor array
        }))
        .filter((audit) => audit.status !== 'Completed')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

      if (currentUser?.role === 'Auditor') {
        Data = Data.filter((audit) => {
          if (!audit.auditor) return false
          
          if (Array.isArray(audit.auditor)) {
            return audit.auditor.some(auditor => 
              auditor.id === currentUser?.uid || auditor.email === currentUser?.email
            )
          } else {
            return audit.auditor.id === currentUser?.uid || audit.auditor.email === currentUser?.email
          }
        })
      }
      setAudits(Data)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    const getActiveFrameworks = () => {
      try {
        const tenantId = currentUser?.tenantId || ''
        const tenantRef = doc(db, 'tenants', tenantId)
        const frameworksCollectionRef = collection(tenantRef, 'active_frameworks')

        const unsubscribe = onSnapshot(frameworksCollectionRef, (snapshot) => {
          const frameworksData = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            frameworks: doc.data().frameworks,
          }))
          const frameworks = frameworksData[0]?.frameworks || []
          setActiveFrameworks(frameworks)
        })

        return unsubscribe
      } catch (error) {
        console.error('Error getting frameworks:', error)
      }
    }

    getActiveFrameworks()
  }, [currentUser])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const testsCollectionRef = collection(tenantRef, 'tests')

    const unsubscribe = onSnapshot(
      testsCollectionRef,
      (snapshot) => {
        const activeTests = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            framework: doc.data().framework,
            status: doc.data().status,
            auditor_review: doc.data().auditor_review,
          }))
          .filter((test) => test.status !== 'Deactivated')

        setTests(activeTests)

        const frameworksToShow =
          currentUser?.role === 'Auditor' && audits.length > 0
            ? audits.flatMap((audit) => audit.framework || [])
            : currentAuditContext
            ? currentAuditContext.framework
            : activeFrameworks

        const filteredTests = activeTests.filter((test) =>
          frameworksToShow.some((framework) => test.framework?.includes(framework))
        )
        setTotalTests(filteredTests.length)
        setPassingTests(filteredTests.filter((test) => test.status === 'Pass').length)
        setFailingTests(filteredTests.filter((test) => test.status === 'Fail').length)

        const isoTests = activeTests.filter((test) => test.framework?.includes('ISO 27001:2022') && test.framework !== 'ISO 27001:2013')
        setTotalISO(isoTests.length)
        setHealthyISOControlsCount(isoTests.filter((test) => test.status === 'Pass').length)
        setUnhealthyISOControlsCount(isoTests.filter((test) => test.status === 'Fail').length)

        const socTests = activeTests.filter((test) => test.framework?.includes('SOC 2'))
        setTotalSOC(socTests.length)
        setHealthySOCControlsCount(socTests.filter((test) => test.status === 'Pass').length)
        setUnhealthySOCControlsCount(socTests.filter((test) => test.status === 'Fail').length)

        const gdprTests = activeTests.filter((test) => test.framework?.includes('GDPR'))
        setTotalGDPR(gdprTests.length)
        setHealthyGDPRControlsCount(gdprTests.filter((test) => test.status === 'Pass').length)
        setUnhealthyGDPRControlsCount(gdprTests.filter((test) => test.status === 'Fail').length)

        const HIPAATests = activeTests.filter((test) => test.framework?.includes('HIPAA'))
        setTotalHIPAA(HIPAATests.length)
        setHealthyHIPAAControlsCount(HIPAATests.filter((test) => test.status === 'Pass').length)
        setUnhealthyHIPAAControlsCount(HIPAATests.filter((test) => test.status === 'Fail').length)
      },
      (error) => {
        console.error('Error getting tests:', error)
      }
    )

    return () => unsubscribe()
  }, [currentUser, activeFrameworks, audits, currentUser?.role])

  const renderFrameworkCard = (framework) => {
    let totalControls, healthyControlsCount, unhealthyControlsCount

    switch (framework) {
      case 'ISO 27001:2022':
        totalControls = totalISO
        healthyControlsCount = healthyISOControlsCount
        unhealthyControlsCount = unhealthyISOControlsCount
        break
      case 'SOC 2':
        totalControls = totalSOC
        healthyControlsCount = healthySOCControlsCount
        unhealthyControlsCount = unhealthySOCControlsCount
        break
      case 'GDPR':
        totalControls = totalGDPR
        healthyControlsCount = healthyGDPRControlsCount
        unhealthyControlsCount = unhealthyGDPRControlsCount
        break
      case 'HIPAA':
        totalControls = totalHIPAA
        healthyControlsCount = healthyHIPAAControlsCount
        unhealthyControlsCount = unhealthyHIPAAControlsCount
        break
      default:
        return null
    }

    return (
      <div className='col-sm-4 mb-5 mb-xl-10'>
        <div className='card card-flush h-lg-80'>
          <div className='card-header'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-4 text-dark'>{framework}</span>
            </h3>
          </div>
          <div className='card-body pt-0'>
            <div className='card-body d-flex align-items-end pt-0 justify-content-end px-0'>
              <div className='d-flex align-items-center flex-column w-100 justify-content-end'>
                <div className='d-flex flex-column flex-sm-row justify-content-between fw-semibold fs-6 text-black opacity-75 w-100 mt-auto mb-2 pt-2'>
                  <span className='text-gray-800'>Passing</span>
                  <span className='fw-bold fs-6'>
                    {totalControls !== 0
                      ? `${((healthyControlsCount / totalControls) * 100).toFixed(0)}%`
                      : '0%'}
                  </span>
                </div>
                <div className='h-12px mx-0 w-100 rounded' style={{backgroundColor: '#DAF9EF'}}>
                  <div
                    className='rounded h-8px'
                    role='progressbar'
                    style={{
                      width:
                        totalControls !== 0
                          ? `${(healthyControlsCount / totalControls) * 100}%`
                          : '0%',
                      backgroundColor: '#0E9F6E',
                    }}
                    aria-valuenow={
                      totalControls !== 0 ? (healthyControlsCount / totalControls) * 100 : 0
                    }
                    aria-valuemin={0}
                    aria-valuemax={100}
                  ></div>
                </div>
              </div>
            </div>
            <div className='d-flex flex-stack'>
              <div className='text-gray-800 fw-semibold fs-6 me-2'>
                <div className='d-inline-block align-middle me-2 mb-1'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#5E6274'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-list-check'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M3.5 5.5l1.5 1.5l2.5 -2.5' />
                    <path d='M3.5 11.5l1.5 1.5l2.5 -2.5' />
                    <path d='M3.5 17.5l1.5 1.5l2.5 -2.5' />
                    <path d='M11 6l9 0' />
                    <path d='M11 12l9 0' />
                    <path d='M11 18l9 0' />
                  </svg>
                </div>
                Total Tests
              </div>
              <div className='d-flex align-items-center'>
                <span className='text-gray-900 fw-bold fs-6'>{totalControls}</span>
              </div>
            </div>
            <div className='my-3'></div>
            <div className='d-flex flex-stack'>
              <div className='text-gray-800 fw-semibold fs-6 me-2'>
                <div className='d-inline-block align-middle me-2 mb-1'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#EE3043'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M12 9v4' />
                    <path d='M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z' />
                    <path d='M12 16h.01' />
                  </svg>
                </div>
                Needs Attention
              </div>
              <div className='d-flex align-items-center'>
                <span className='text-gray-900 fw-bold fs-6'>{unhealthyControlsCount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (
    (currentAuditContext &&
      currentAuditContext.framework &&
      currentAuditContext.framework.length > 0) ||
    currentUser?.role === 'Auditor'
  ) {
    const auditTests = tests.filter((test) => {
      const testFrameworks = test.framework.split(',').map((fw) => fw.trim())
      if (currentAuditContext?.framework) {
        return currentAuditContext.framework.some((auditFramework) =>
          testFrameworks.includes(auditFramework)
        )
      }

      if (currentUser?.role === 'Auditor') {
        return audits.some((audit) =>
          testFrameworks.includes(audit.framework[0])
        )
      }

      return false
    })
    const currentAuditId = currentAuditContext?.id || (audits && audits.length > 0 ? audits[0].id : null)
    const totalTests = auditTests.length
    const approvedCount = auditTests.filter((test) => getReviewStatus(test.auditor_review, currentAuditId) === 'Approved').length
    const readyForAuditCount = auditTests.filter((test) => {
      const reviewStatus = getReviewStatus(test.auditor_review, currentAuditId)
      return test.status === 'Pass' && reviewStatus !== 'Approved' && reviewStatus !== 'Not applicable' && reviewStatus !== 'Flagged'
    }).length
    const notReadyForAuditCount = auditTests.filter((test) => test.status === 'Fail').length
    const flaggedCount = auditTests.filter((test) => getReviewStatus(test.auditor_review, currentAuditId) === 'Flagged').length
    const notApplicableCount = auditTests.filter(
      (test) => getReviewStatus(test.auditor_review, currentAuditId) === 'Not applicable'
    ).length

    const approvedPercentage = totalTests !== 0 ? (approvedCount / totalTests) * 100 : 0
    const readyForAuditPercentage = totalTests !== 0 ? (readyForAuditCount / totalTests) * 100 : 0
    const notReadyForAuditPercentage =
      totalTests !== 0 ? (notReadyForAuditCount / totalTests) * 100 : 0
    const flaggedPercentage = totalTests !== 0 ? (flaggedCount / totalTests) * 100 : 0
    const notApplicablePercentage = totalTests !== 0 ? (notApplicableCount / totalTests) * 100 : 0

    return (
      <div className='col-sm-12 mb-5 mb-xl-10'>
        <div className='card card-flush h-lg-80'>
          <div className='card-header'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-4 text-dark'>Audit Summary</span>
            </h3>
          </div>
          <div className='card-body pt-0'>
            <div className='card-body d-flex align-items-end pt-0 justify-content-end px-0'>
              <div className='d-flex align-items-center flex-column w-100 justify-content-end'>
                <div className='d-flex flex-column flex-sm-row justify-content-between fw-semibold fs-6 text-black opacity-75 w-100 mt-auto mb-2'>
                  <span className='text-gray-800 fs-7'>Progress by status</span>
                  {/* <span className='fw-bold fs-6'>
                    {totalTests} Total Tests
                  </span> */}
                </div>

                <div
                  className='mx-0 w-100 rounded d-flex overflow-hidden'
                  style={{backgroundColor: '#E5E7EB', height: '8px'}}
                >
                  {approvedCount > 0 && (
                    <div
                      style={{
                        width: `${approvedPercentage}%`,
                        backgroundColor: '#0E9F6E',
                        minWidth: approvedCount > 0 ? '2px' : '0px',
                        height: '100%',
                        borderRadius: '4px',
                      }}
                      title={`Approved: ${approvedCount} (${approvedPercentage.toFixed(1)}%)`}
                    ></div>
                  )}

                  {readyForAuditCount > 0 && (
                    <>
                      <div style={{width: '6px', backgroundColor: 'white', height: '100%'}}></div>
                      <div
                        style={{
                          width: `${readyForAuditPercentage}%`,
                          backgroundColor: '#3B82F6',
                          minWidth: readyForAuditCount > 0 ? '2px' : '0px',
                          height: '100%',
                          borderRadius: '4px',
                        }}
                        title={`Ready for audit: ${readyForAuditCount} (${readyForAuditPercentage.toFixed(
                          1
                        )}%)`}
                      ></div>
                    </>
                  )}

                  {notReadyForAuditCount > 0 && (
                    <>
                      <div style={{width: '6px', backgroundColor: 'white', height: '100%'}}></div>
                      <div
                        style={{
                          width: `${notReadyForAuditPercentage}%`,
                          backgroundColor: '#EE3043',
                          minWidth: notReadyForAuditCount > 0 ? '2px' : '0px',
                          height: '100%',
                          borderRadius: '4px',
                        }}
                        title={`Not ready for audit: ${notReadyForAuditCount} (${notReadyForAuditPercentage.toFixed(
                          1
                        )}%)`}
                      ></div>
                    </>
                  )}

                  {flaggedCount > 0 && (
                    <>
                      <div style={{width: '6px', backgroundColor: 'white', height: '100%'}}></div>
                      <div
                        style={{
                          width: `${flaggedPercentage}%`,
                          backgroundColor: '#B85407',
                          minWidth: flaggedCount > 0 ? '2px' : '0px',
                          height: '100%',
                          borderRadius: '4px',
                        }}
                        title={`Flagged: ${flaggedCount} (${flaggedPercentage.toFixed(1)}%)`}
                      ></div>
                    </>
                  )}

                  {notApplicableCount > 0 && (
                    <>
                      <div style={{width: '6px', backgroundColor: 'white', height: '100%'}}></div>
                      <div
                        style={{
                          width: `${notApplicablePercentage}%`,
                          backgroundColor: '#6b7280',
                          minWidth: notApplicableCount > 0 ? '2px' : '0px',
                          height: '100%',
                          borderRadius: '4px',
                        }}
                        title={`Not applicable: ${notApplicableCount} (${notApplicablePercentage.toFixed(
                          1
                        )}%)`}
                      ></div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className='row g-4 d-flex justify-content-between'>
              <div className='col-md-3'>
                <div className='d-flex flex-stack mb-4'>
                  <div className='text-gray-700 fw-semibold fs-6 me-2'>
                    <div className='d-inline-block align-middle me-2 mb-1'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='#000000'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-list-check'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M3.5 5.5l1.5 1.5l2.5 -2.5' />
                        <path d='M3.5 11.5l1.5 1.5l2.5 -2.5' />
                        <path d='M3.5 17.5l1.5 1.5l2.5 -2.5' />
                        <path d='M11 6l9 0' />
                        <path d='M11 12l9 0' />
                        <path d='M11 18l9 0' />
                      </svg>
                    </div>
                    Total Tests
                  </div>
                  <div className='d-flex align-items-center justify-content-start'>
                    <span className='text-gray-900 fw-bold fs-6'>{totalTests}</span>
                  </div>
                </div>
                <div className='d-flex flex-stack'>
                  <div className='text-gray-700 fw-semibold fs-6 me-2'>
                    <div className='d-inline-block align-middle me-2 mb-1'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='#0E9F6E'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-circle-check'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                        <path d='M9 12l2 2l4 -4' />
                      </svg>
                    </div>
                    Approved
                  </div>
                  <div className='d-flex align-items-center justify-content-start'>
                    <span className='text-gray-900 fw-bold fs-6'>{approvedCount}</span>
                  </div>
                </div>
              </div>
              <div className='col-md-1'></div>
              <div className='col-md-3'>
                <div className='d-flex flex-stack mb-4'>
                  <div className='text-gray-700 fw-semibold fs-6 me-2'>
                    <div className='d-inline-block align-middle me-2 mb-1'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='#3B82F6'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-clipboard-text'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2' />
                        <path d='M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z' />
                        <path d='M9 12h6' />
                        <path d='M9 16h6' />
                      </svg>
                    </div>
                    Ready for audit
                  </div>
                  <div className='d-flex align-items-center'>
                    <span className='text-gray-900 fw-bold fs-6'>{readyForAuditCount}</span>
                  </div>
                </div>
                <div className='d-flex flex-stack'>
                  <div className='text-gray-700 fw-semibold fs-6 me-2'>
                    <div className='d-inline-block align-middle me-2 mb-1'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='#EE3043'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-clipboard-x'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2' />
                        <path d='M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z' />
                        <path d='M10 12l4 4m0 -4l-4 4' />
                      </svg>
                    </div>
                    Not ready for audit
                  </div>
                  <div className='d-flex align-items-center'>
                    <span className='text-gray-900 fw-bold fs-6'>{notReadyForAuditCount}</span>
                  </div>
                </div>
              </div>

              <div className='col-md-1'></div>
              <div className='col-md-3'>
                <div className='d-flex flex-stack mb-4'>
                  <div className='text-gray-700 fw-semibold fs-6 me-2'>
                    <div className='d-inline-block align-middle me-2 mb-1'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='#B85407'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-alert-circle'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                        <path d='M12 8v4' />
                        <path d='M12 16h.01' />
                      </svg>
                    </div>
                    Flagged by auditor
                  </div>
                  <div className='d-flex align-items-center'>
                    <span className='text-gray-900 fw-bold fs-6'>{flaggedCount}</span>
                  </div>
                </div>
                <div className='d-flex flex-stack'>
                  <div className='text-gray-700 fw-semibold fs-6 me-2'>
                    <div className='d-inline-block align-middle me-2 mb-1'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='#6b7280'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-clipboard-off'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M5.575 5.597a2 2 0 0 0 -.575 1.403v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2m0 -4v-8a2 2 0 0 0 -2 -2h-2' />
                        <path d='M9 5a2 2 0 0 1 2 -2h2a2 2 0 1 1 0 4h-2' />
                        <path d='M3 3l18 18' />
                      </svg>
                    </div>
                    Not applicable
                  </div>
                  <div className='d-flex align-items-center'>
                    <span className='text-gray-900 fw-bold fs-6'>{notApplicableCount}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='row gx-4 gx-xl-10'>
      <div className='col-sm-4 mb-5 mb-xl-10'>
        <div className='card card-flush h-lg-80'>
          <div className='card-header pt-0'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-4 text-dark'>All Tests</span>
            </h3>
          </div>
          <div className='card-body pt-0'>
            <div className='card-body d-flex align-items-end pt-0 justify-content-end px-0'>
              <div className='d-flex align-items-center flex-column w-100 justify-content-end'>
                <div className='d-flex flex-column flex-sm-row justify-content-between fw-semibold fs-6 text-black opacity-75 w-100 mt-auto mb-2 pt-2'>
                  <span className='text-gray-800'>Passing</span>
                  <span className='fw-bold fs-6'>
                    {totalTest !== 0 ? `${((passingTests / totalTest) * 100).toFixed(0)}%` : '0%'}
                  </span>
                </div>
                <div className='h-12px mx-0 w-100 rounded' style={{backgroundColor: '#DAF9EF'}}>
                  <div
                    className='rounded h-8px'
                    role='progressbar'
                    style={{
                      width: totalTest !== 0 ? `${(passingTests / totalTest) * 100}%` : '0%',
                      backgroundColor: '#0E9F6E',
                    }}
                    aria-valuenow={totalTest !== 0 ? (passingTests / totalTest) * 100 : 0}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  ></div>
                </div>
              </div>
            </div>
            <div className='d-flex flex-stack'>
              <div className='text-gray-800 fw-semibold fs-6 me-2'>
                <div className='d-inline-block align-middle me-2 mb-1'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#5E6274'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-list-check'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M3.5 5.5l1.5 1.5l2.5 -2.5' />
                    <path d='M3.5 11.5l1.5 1.5l2.5 -2.5' />
                    <path d='M3.5 17.5l1.5 1.5l2.5 -2.5' />
                    <path d='M11 6l9 0' />
                    <path d='M11 12l9 0' />
                    <path d='M11 18l9 0' />
                  </svg>
                </div>
                Total Tests
              </div>
              <div className='d-flex align-items-center'>
                <span className='text-gray-900 fw-bold fs-6'>{totalTest}</span>
              </div>
            </div>
            <div className='my-3'></div>
            <div className='d-flex flex-stack'>
              <div className='text-gray-800 fw-semibold fs-6 me-2'>
                <div className='d-inline-block align-middle me-2 mb-1'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#EE3043'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M12 9v4' />
                    <path d='M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z' />
                    <path d='M12 16h.01' />
                  </svg>
                </div>
                Needs Attention
              </div>
              <div className='d-flex align-items-center'>
                <span className='text-gray-900 fw-bold fs-6'>{failingTests}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {(() => {
        const frameworksToShow =
          currentUser?.role === 'Auditor' && audits.length > 0
            ? audits.flatMap((audit) => audit.framework || [])
            : activeFrameworks

        return (
          <>
            {frameworksToShow.includes('ISO 27001:2022') && renderFrameworkCard('ISO 27001:2022')}
            {frameworksToShow.includes('SOC 2') && renderFrameworkCard('SOC 2')}
            {frameworksToShow.includes('GDPR') && renderFrameworkCard('GDPR')}
            {frameworksToShow.includes('HIPAA') && renderFrameworkCard('HIPAA')}
          </>
        )
      })()}
      {(() => {
        const frameworksToShow =
          currentUser?.role === 'Auditor' && audits.length > 0
            ? audits.flatMap((audit) => audit.framework || [])
            : activeFrameworks
        return frameworksToShow.length < 2
      })() && (
        <div className='col d-flex align-items-stretch mb-5 mb-xl-10'>
          <div className='card flex-fill'>
            <div className='card-body d-flex flex-column justify-content-center align-items-center'>
              <img src='/media/icon/framework.svg' alt='Framework icon' className='mb-4' />
              <h5 className='card-title fw-bold text-center mb-4'>
                {(() => {
                  const frameworksToShow =
                    currentUser?.role === 'Auditor' && audits.length > 0
                      ? audits.flatMap((audit) => audit.framework || [])
                      : activeFrameworks
                  return frameworksToShow.length <= 0
                    ? 'Tests Unavailable'
                    : 'Enable other frameworks'
                })()}
              </h5>
              <p className='card-text fw-semibold text-center'>
                <span style={{cursor: 'pointer', color: '#0e9f6e'}} onClick={toggleCrispChat}>
                  Contact sales
                </span>{' '}
                to enable other frameworks
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardHeader
